/**
 * Pinia-Store für strukturierte Collections (Produkt-/Katalog-Pflege).
 *
 *   loadCollections(projectId)         → welche Collections hat das Projekt
 *   open(projectId, collection)        → Schema + Items laden (für das Grid)
 *   create/update/remove/reorder       → CRUD, danach Items neu laden
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, ApiError } from '@/lib/api'
import type {
  CollectionSummary,
  CollectionItem,
  CollectionFeldwert,
  JsonSchema,
} from '@/types/api'
import { useToast } from '@/composables/useToast'
import { uebersetze } from '@/lib/uebersetzen'
import { fehlersatz } from '@/lib/fehlersatz'
import { dateifehler } from '@/lib/dateifehler'
import { veroeffentlichenSchluessel } from '@/lib/veroeffentlichen-meldungen'

/**
 * Ein Fehler beim Katalog als Satz in der Sprache des Lesers.
 *
 * Bis zum 08.10.2026 stand in der Meldung, was der Browser wusste („HTTP 409
 * on /api/v1/…"), und die Überschrift darüber war immer deutsch. Zwei Fälle
 * kennt nur der Katalog: Den Namen gibt es schon, und an einer Kategorie
 * hängen noch Einträge. Alles andere weiß `fehlersatz()`.
 */
function katalogfehler(e: unknown, was = uebersetze('katalog.was')): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: unknown; value?: unknown } | null
    if (body?.error === 'already_exists') {
      return uebersetze('katalog.fehler.gibt_es_schon', { wert: String(body.value ?? '') })
    }
    if (body?.error === 'reassign_required') return uebersetze('katalog.fehler.umhaengen_noetig')
  }
  return fehlersatz(e, was)
}

export const useCollectionsStore = defineStore('collections', () => {
  const toast = useToast()

  const projectId = ref<string>('')
  const collections = ref<CollectionSummary[]>([])
  const activeKey = ref<string>('')
  const schema = ref<JsonSchema | null>(null)
  const items = ref<CollectionItem[]>([])
  const loading = ref(false)
  const saving = ref(false)
  const error = ref<string | null>(null)

  // Kategorie-Verwaltung (enum des Gruppen-Felds, z.B. "hersteller").
  const groupField = ref<string | null>(null)
  const categories = ref<string[]>([])
  const categoryCounts = ref<Record<string, number>>({})
  const categoryImages = ref<Record<string, string>>({})
  /** Kategorie, deren Bild gerade hochgeladen wird (für Spinner). */
  const categoryImageUploading = ref<string | null>(null)

  // Veröffentlichen: Saves schreiben sofort auf Platte (dirty=true), Website
  // wird erst beim „Jetzt veröffentlichen" gebaut (publish()).
  const dirty = ref(false)
  const publishing = ref(false)
  function markDirty(): void { dirty.value = true }

  const hasCollections = computed(() => collections.value.length > 0)

  /** Feld-Key, dessen Wert als Karten-Titel dient (aus Schema x-cms-ui). */
  const labelField = computed<string>(() => {
    const ui = schema.value?.['x-cms-ui'] as Record<string, unknown> | undefined
    const v = ui?.['item_label_field']
    return typeof v === 'string' && v !== '' ? v : 'titel'
  })

  /**
   * Anzeige-Name des Gruppen-Felds (z.B. „Hersteller") — aus dem Schema-Label
   * des Gruppen-Felds. So heißt die Gruppierung in Leiste + Formular gleich.
   * Fallback „Kategorie", wenn kein Label gesetzt ist.
   */
  const groupLabel = computed<string>(() => {
    const f = groupField.value
    if (!f) return uebersetze('katalog.kategorie')
    const sch = schema.value as Record<string, unknown> | null
    const props = sch?.['properties'] as Record<string, unknown> | undefined
    const def = props?.[f] as Record<string, unknown> | undefined
    const ui = def?.['x-cms-ui'] as Record<string, unknown> | undefined
    const l = ui?.['label']
    return typeof l === 'string' && l !== '' ? l : uebersetze('katalog.kategorie')
  })

  /**
   * Wie die Einträge dieser Sammlung heißen, in der Mehrzahl: „Kataloge",
   * „Leistungen", „Gerichte". Steht in der Gruppenüberschrift über der Liste.
   *
   * Vorher stand dort fest „Alle Kataloge", weil LGH lange die einzige Sammlung
   * war. Bei einer Speisekarte wäre das schlicht falsch, und das Wort steht
   * zugleich in der gedruckten Anleitung der Kundin — es darf sich also nicht
   * einfach ändern, nur weil eine zweite Sammlung dazukommt.
   */
  const itemNamePlural = computed<string>(() => {
    const ui = schema.value?.['x-cms-ui'] as Record<string, unknown> | undefined
    const v = ui?.['item_name_plural']
    return typeof v === 'string' && v !== '' ? v : uebersetze('katalog.eintraege')
  })

  const imageField = computed<string | null>(() => {
    const ui = schema.value?.['x-cms-ui'] as Record<string, unknown> | undefined
    const v = ui?.['item_image_field']
    return typeof v === 'string' && v !== '' ? v : null
  })

  /** Felder, die als Meta-Chips auf der Karte erscheinen (z.B. hersteller, typ). */
  const metaFields = computed<string[]>(() => {
    const ui = schema.value?.['x-cms-ui'] as Record<string, unknown> | undefined
    const v = ui?.['item_meta_fields']
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  })

  async function loadCollections(id: string): Promise<void> {
    projectId.value = id
    loading.value = true
    error.value = null
    try {
      const res = await api.listCollections(id)
      collections.value = res.collections
    } catch (e) {
      error.value = katalogfehler(e, uebersetze('katalog.was_sammlung'))
      collections.value = []
    } finally {
      loading.value = false
    }
  }

  async function open(id: string, collection: string): Promise<void> {
    projectId.value = id
    activeKey.value = collection
    loading.value = true
    error.value = null
    try {
      const [sch, list, cats] = await Promise.all([
        api.collectionSchema(id, collection),
        api.collectionItems(id, collection),
        api.collectionCategories(id, collection),
      ])
      schema.value = sch.schema
      items.value = list.items
      groupField.value = cats.field
      categories.value = cats.values
      categoryCounts.value = cats.counts
      categoryImages.value = cats.images ?? {}
    } catch (e) {
      error.value = katalogfehler(e, uebersetze('katalog.was_sammlung'))
    } finally {
      loading.value = false
    }
    // Offene (nicht veröffentlichte) Änderungen aus letzter Sitzung erkennen.
    try {
      dirty.value = (await api.publishStatus(id)).dirty
    } catch {
      dirty.value = false
    }
  }

  async function reload(): Promise<void> {
    if (projectId.value && activeKey.value) {
      const [list, cats] = await Promise.all([
        api.collectionItems(projectId.value, activeKey.value),
        api.collectionCategories(projectId.value, activeKey.value),
      ])
      items.value = list.items
      groupField.value = cats.field
      categories.value = cats.values
      categoryCounts.value = cats.counts
      categoryImages.value = cats.images ?? {}
    }
  }

  /**
   * Weist der Server einen einzelnen Wert zurueck (422), fliegt der Fehler
   * weiter zum Formular: Es schreibt die Meldung an das betroffene Feld, statt
   * eine Kurzmeldung am Bildschirmrand einzublenden, die nicht sagt, welches
   * Feld gemeint ist.
   */
  function istFeldfehler(e: unknown): boolean {
    return e instanceof ApiError && e.status === 422
  }

  async function create(data: Record<string, CollectionFeldwert>): Promise<string | null> {
    saving.value = true
    try {
      const res = await api.createCollectionItem(projectId.value, activeKey.value, data)
      await reload()
      markDirty()
      toast.success(uebersetze('katalog.eintrag_angelegt'))
      return res.id
    } catch (e) {
      if (istFeldfehler(e)) throw e
      toast.error(uebersetze('katalog.fehler.anlegen'), katalogfehler(e))
      return null
    } finally {
      saving.value = false
    }
  }

  async function update(itemId: string, data: Record<string, CollectionFeldwert>): Promise<boolean> {
    saving.value = true
    try {
      await api.updateCollectionItem(projectId.value, activeKey.value, itemId, data)
      await reload()
      markDirty()
      toast.success(uebersetze('katalog.gespeichert'))
      return true
    } catch (e) {
      if (istFeldfehler(e)) throw e
      toast.error(uebersetze('errors.save_failed'), katalogfehler(e))
      return false
    } finally {
      saving.value = false
    }
  }

  async function remove(itemId: string): Promise<boolean> {
    saving.value = true
    try {
      await api.deleteCollectionItem(projectId.value, activeKey.value, itemId)
      await reload()
      markDirty()
      toast.success(uebersetze('katalog.geloescht'))
      return true
    } catch (e) {
      toast.error(uebersetze('katalog.fehler.loeschen'), katalogfehler(e))
      return false
    } finally {
      saving.value = false
    }
  }

  /** Optimistisch neu sortieren + persistieren. */
  async function reorder(orderedIds: string[]): Promise<void> {
    const byId = new Map(items.value.map((i) => [i.id, i]))
    items.value = orderedIds.map((id) => byId.get(id)).filter((i): i is CollectionItem => !!i)
    saving.value = true
    try {
      await api.reorderCollection(projectId.value, activeKey.value, orderedIds)
      markDirty()
      toast.success(uebersetze('katalog.reihenfolge_gespeichert'))
    } catch (e) {
      toast.error(uebersetze('katalog.fehler.sortieren'), katalogfehler(e))
      await reload()
    } finally {
      saving.value = false
    }
  }

  function getItem(itemId: string): CollectionItem | undefined {
    return items.value.find((i) => i.id === itemId)
  }

  /** Gruppen-Feld eines Items setzen (z.B. Drag in andere Hersteller-Gruppe). */
  async function setItemCategory(itemId: string, value: string): Promise<boolean> {
    const field = groupField.value
    const item = getItem(itemId)
    if (!field || !item) return false
    if (item.data[field] === value) return true
    return update(itemId, { ...item.data, [field]: value })
  }

  // ── Kategorie-Verwaltung ────────────────────────────────────────────────

  async function addCategory(value: string): Promise<boolean> {
    try {
      const res = await api.addCategory(projectId.value, activeKey.value, value.trim())
      categories.value = res.values
      markDirty()
      toast.success(uebersetze('katalog.kategorie_angelegt'))
      return true
    } catch (e) {
      toast.error(uebersetze('katalog.fehler.anlegen'), katalogfehler(e, uebersetze('katalog.was_kategorie')))
      return false
    }
  }

  async function renameCategory(from: string, to: string): Promise<boolean> {
    try {
      const res = await api.renameCategory(projectId.value, activeKey.value, from, to.trim())
      categories.value = res.values
      await reload()
      markDirty()
      toast.success(res.items_updated > 0
        ? uebersetze('katalog.umbenannt_mit', { n: res.items_updated }, res.items_updated)
        : uebersetze('katalog.umbenannt'))
      return true
    } catch (e) {
      toast.error(uebersetze('katalog.fehler.umbenennen'), katalogfehler(e, uebersetze('katalog.was_kategorie')))
      return false
    }
  }

  async function deleteCategory(value: string, reassignTo?: string): Promise<boolean> {
    try {
      const res = await api.deleteCategory(projectId.value, activeKey.value, value, reassignTo)
      categories.value = res.values
      await reload()
      markDirty()
      toast.success(res.items_updated > 0
        ? uebersetze('katalog.geloescht_umgehaengt', { n: res.items_updated }, res.items_updated)
        : uebersetze('katalog.kategorie_geloescht'))
      return true
    } catch (e) {
      toast.error(uebersetze('katalog.fehler.loeschen'), katalogfehler(e, uebersetze('katalog.was_kategorie')))
      return false
    }
  }

  /** Bild einer Kategorie hochladen + zuweisen (WebP-Konvertierung im Backend). */
  async function setCategoryImage(name: string, file: File): Promise<boolean> {
    categoryImageUploading.value = name
    try {
      const up = await api.uploadImage(projectId.value, file, { subdir: 'categories', defer: true })
      const res = await api.setCategoryImage(projectId.value, activeKey.value, name, up.value)
      if (res.image) {
        categoryImages.value = { ...categoryImages.value, [name]: res.image }
      } else {
        const next = { ...categoryImages.value }
        delete next[name]
        categoryImages.value = next
      }
      markDirty()
      toast.success(uebersetze('katalog.bild_gesetzt'))
      return true
    } catch (e) {
      toast.error(uebersetze('katalog.fehler.bild'), dateifehler(e))
      return false
    } finally {
      categoryImageUploading.value = null
    }
  }

  /** Bild einer Kategorie entfernen. */
  async function removeCategoryImage(name: string): Promise<boolean> {
    try {
      await api.setCategoryImage(projectId.value, activeKey.value, name, '')
      const next = { ...categoryImages.value }
      delete next[name]
      categoryImages.value = next
      markDirty()
      toast.success(uebersetze('katalog.bild_entfernt'))
      return true
    } catch (e) {
      toast.error(uebersetze('katalog.fehler.bild_entfernen'), katalogfehler(e, uebersetze('katalog.was_kategorie')))
      return false
    }
  }

  /** Alle aufgeschobenen Änderungen veröffentlichen (commit+push+build, gebündelt). */
  async function publish(): Promise<boolean> {
    if (publishing.value) return false
    publishing.value = true
    try {
      const res = await api.publish(projectId.value)
      if (res.published) {
        dirty.value = false
        // Wohin veröffentlicht wurde, muss dastehen. „Website wird aktualisiert"
        // stimmt nur im Kunden-Kiosk; als Admin oder Redakteurin geht es in die
        // Vorschau, und die öffentliche Seite bleibt, wie sie war. Am 25.08.2026
        // hat genau das jemanden eine Viertelstunde Suche gekostet.
        if (!res.committed) {
          toast.success(uebersetze('katalog.alles_aktuell'))
        } else if (res.live) {
          toast.success(uebersetze('katalog.veroeffentlicht_live'))
        } else {
          toast.success(uebersetze('katalog.veroeffentlicht_vorschau'))
        }
        return true
      }
      toast.error(uebersetze('katalog.fehler.veroeffentlichen'), uebersetze(veroeffentlichenSchluessel(res.error)))
      return false
    } catch (e) {
      // 409 = es läuft schon eine Veröffentlichung.
      const satz = e instanceof ApiError && e.status === 409
        ? uebersetze('publish.fehler.beschaeftigt')
        : katalogfehler(e)
      toast.error(uebersetze('katalog.fehler.veroeffentlichen'), satz)
      return false
    } finally {
      publishing.value = false
    }
  }

  async function reorderCategories(order: string[]): Promise<void> {
    const prev = categories.value.slice()
    categories.value = order
    try {
      const res = await api.reorderCategories(projectId.value, activeKey.value, order)
      categories.value = res.values
      markDirty()
      toast.success(uebersetze('katalog.reihenfolge_gespeichert'))
    } catch (e) {
      categories.value = prev
      toast.error(uebersetze('katalog.fehler.sortieren'), katalogfehler(e, uebersetze('katalog.was_kategorie')))
    }
  }

  return {
    projectId, collections, activeKey, schema, items,
    loading, saving, error,
    groupField, categories, categoryCounts, categoryImages, categoryImageUploading,
    dirty, publishing,
    hasCollections, labelField, imageField, metaFields, groupLabel, itemNamePlural,
    loadCollections, open, reload, create, update, remove, reorder, getItem,
    setItemCategory, addCategory, renameCategory, deleteCategory, reorderCategories,
    setCategoryImage, removeCategoryImage, publish,
  }
})
